import { Quote, RotateCw } from 'lucide-react';

/**
 * SlideVisual — the diagram on a slide, drawn and animated by the app.
 *
 * The owner's note on the NotebookLM comparison: "Nobody on earth does a slide
 * deck only on bullet points." Every slide the model returns now carries a
 * typed `visual`, and this renders it as a real diagram that builds itself in
 * front of the student — a flow, a cycle, a comparison, a labelled thing, a
 * timeline, a hierarchy, numbers, or a line worth remembering.
 *
 * Everything is CSS + SVG: no image credits, no copyright risk, and it animates,
 * which a slide deck full of pictures never can.
 */

const step = (i) => ({ animationDelay: `${120 + i * 110}ms` });

function Flow({ data, cycle }) {
  const steps = (data.steps || []).slice(0, 6);
  // Bento tiles, not a list with arrows: an arrow at the end of a row reads as a
  // link and a child taps it expecting to go somewhere. The order lives in the
  // numbered chip, and the tiles are plainly panels of text.
  const span = (i) => (steps.length % 2 === 1 && i === steps.length - 1 ? 'sm:col-span-2' : '');
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
      {steps.map((s, i) => (
        <div key={i} style={step(i)}
             className={`relative rounded-2xl bg-white/12 backdrop-blur-sm border border-white/20 p-3.5 sm:p-4 opacity-0 animate-slide-up ${span(i)}`}>
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-white/25 font-display text-[12px] font-bold text-white mb-1.5">
            {cycle && i === steps.length - 1 ? <RotateCw className="w-3.5 h-3.5" /> : i + 1}
          </span>
          <p className="font-heading text-[15px] sm:text-base text-white leading-snug">{s.label}</p>
          {s.note && <p className="font-body text-[13px] sm:text-sm text-white/75 mt-1 leading-snug">{s.note}</p>}
        </div>
      ))}
    </div>
  );
}

function Compare({ data }) {
  const sides = [data.left || {}, data.right || {}];
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {sides.map((side, i) => (
        <div key={i} className="rounded-2xl bg-white/12 border border-white/20 p-3 opacity-0 animate-slide-up"
             style={step(i)}>
          <p className="font-heading text-[13px] sm:text-[15px] text-white uppercase tracking-wide">{side.title}</p>
          <ul className="mt-2 space-y-1.5">
            {(side.points || []).slice(0, 5).map((p, j) => (
              <li key={j} className="font-body text-[13px] sm:text-[14px] text-white/90 leading-snug flex gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-white/60 flex-shrink-0" />{p}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function Parts({ data }) {
  const labels = (data.labels || []).slice(0, 6);
  return (
    <div className="relative">
      <div className="mx-auto w-fit rounded-full bg-white/20 border border-white/30 px-5 py-3 mb-3 opacity-0 animate-slide-up"
           style={step(0)}>
        <p className="font-heading text-[15px] sm:text-lg text-white text-center">{data.center}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {labels.map((l, i) => (
          <div key={i} className="rounded-xl bg-white/10 border-l-2 border-white/50 px-3 py-2 opacity-0 animate-slide-up"
               style={step(i + 1)}>
            <p className="font-heading text-[14px] sm:text-[15px] text-white">{l.label}</p>
            {l.note && <p className="font-body text-[12.5px] sm:text-[13px] text-white/75 mt-0.5 leading-snug">{l.note}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Timeline({ data }) {
  const events = (data.events || []).slice(0, 6);
  return (
    <div className="relative pl-5 border-l-2 border-white/30 space-y-4">
      {events.map((e, i) => (
        <div key={i} className="relative opacity-0 animate-slide-up" style={step(i)}>
          <span className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-white shadow" />
          <p className="font-heading text-[14px] sm:text-[15px] text-white">{e.when}</p>
          <p className="font-body text-[13px] sm:text-[14px] text-white/80 leading-snug">{e.what}</p>
        </div>
      ))}
    </div>
  );
}

function Layers({ data }) {
  const layers = (data.layers || []).slice(0, 5);
  return (
    <div className="space-y-2">
      {layers.map((l, i) => (
        <div key={i}
             className="rounded-xl bg-white/15 border border-white/20 px-3 py-2 mx-auto opacity-0 animate-slide-up"
             style={{ ...step(i), width: `${100 - i * 9}%` }}>
          <p className="font-heading text-[14px] sm:text-[15px] text-white">{l.label}</p>
          {l.note && <p className="font-body text-[12.5px] sm:text-[13px] text-white/75 leading-snug">{l.note}</p>}
        </div>
      ))}
    </div>
  );
}

function Stats({ data }) {
  const items = (data.items || []).slice(0, 4);
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {items.map((it, i) => (
        <div key={i} className="rounded-2xl bg-white/12 border border-white/20 p-3 text-center opacity-0 animate-slide-up"
             style={step(i)}>
          <p className="font-display text-2xl sm:text-4xl font-bold text-white tracking-tight">{it.value}</p>
          <p className="font-body text-[13px] sm:text-[14px] text-white/80 mt-1 leading-snug">{it.label}</p>
        </div>
      ))}
    </div>
  );
}

function Quoted({ data }) {
  return (
    <figure className="rounded-2xl bg-white/12 border border-white/20 p-4 opacity-0 animate-slide-up" style={step(0)}>
      <Quote className="w-5 h-5 text-white/50" />
      <blockquote className="font-display text-base sm:text-2xl text-white leading-snug mt-2">{data.text}</blockquote>
      {data.who && <figcaption className="font-body text-[13px] text-white/70 mt-2">— {data.who}</figcaption>}
    </figure>
  );
}

const RENDERERS = {
  flow: Flow,
  cycle: (p) => <Flow {...p} cycle />,
  compare: Compare,
  parts: Parts,
  timeline: Timeline,
  layers: Layers,
  stats: Stats,
  quote: Quoted,
};

export function SlideVisual({ visual, replayKey }) {
  if (!visual?.type || !visual?.data) return null;
  const Renderer = RENDERERS[visual.type];
  if (!Renderer) return null;
  return (
    <div key={replayKey} data-testid={`slide-visual-${visual.type}`}>
      <Renderer data={visual.data} />
      {visual.caption && (
        <p className="font-body text-[12.5px] sm:text-[13px] text-white/65 mt-3 italic">{visual.caption}</p>
      )}
    </div>
  );
}

export default SlideVisual;
